/**
 * /ndt-inspection/welders — the welder register (GET /ndt-inspection/welders).
 *
 * A welder need not be a FieldIQ user: welders are often the customer's or a
 * contractor's hands. Each row carries the API's `qualStatus` — the worst
 * standing across the welder's qualifications, where a qualification's
 * standing is the worse of its expiry and its six-month process continuity —
 * "Expired" when an expiry day has passed, "Lapsed" when continuity has
 * (ndtQualSkin; the same words as the welder page and the Overview). Null means the welder holds no qualification at all, and renders as
 * "No quals" rather than as a status.
 *
 * Search is the server's (`q` over name, stamp and employer).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, HardHat, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtWelders } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, ndtQualSkin, type NdtSkin } from '../../lib/ndt-inspection';
import type { NdtWelder } from '../../types/ndt-inspection';
import { WelderDialog } from './NdtDialogs';
import { NdtBadge, NdtEmpty, NdtPanel, NdtRegister, type NdtColumn } from './NdtBits';

const NO_QUALS: NdtSkin = { label: 'No quals', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' };
/**
 * The acceptance codes the welder is qualified under (CSA Z662, API 1104), as
 * the API labels them, each once, in the order the API listed the quals. A
 * welder with no qualification has no code: `—`.
 */
export function welderCodes(w: Pick<NdtWelder, 'quals'>): string {
  const seen: string[] = [];
  for (const q of w.quals ?? []) {
    const label = q.codeLabel || q.code;
    if (label && !seen.includes(label)) seen.push(label);
  }
  return seen.length ? seen.join(', ') : '—';
}

export const welderSkin = (w: Pick<NdtWelder, 'qualStatus'>) => (w.qualStatus === null ? NO_QUALS : ndtQualSkin(w.qualStatus));

export default function WeldersScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const listQuery = useNdtWelders(q ? { q } : undefined);
  const allQuery = useNdtWelders();
  const welders = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (w: NdtWelder) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const canAdd = canAdminNdt(user?.role);

  const columns: NdtColumn<NdtWelder>[] = [
    { key: 'name', header: 'Welder', render: (w) => <span className="font-extrabold text-white">{w.name || '—'}</span> },
    { key: 'stamp', header: 'Stamp', render: (w) => <span className="font-mono">{w.welderStamp || '—'}</span> },
    { key: 'employer', header: 'Employer', render: (w) => w.employer || '—' },
    { key: 'code', header: 'Code', render: (w) => <span data-testid="ndt-welder-code">{welderCodes(w)}</span> },
    { key: 'quals', header: 'Current / total quals', align: 'right', render: (w) => `${w.currentQualCount} / ${w.qualCount}` },
    { key: 'status', header: 'Standing', render: (w) => <NdtBadge skin={welderSkin(w)} value={w.qualStatus} /> },
  ];

  return (
    <div data-testid="ndt-welders-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Welders & Quals"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add welder
            </button>
          ) : undefined
        }
        stats={[
          { icon: <HardHat className="size-4" aria-hidden="true" />, label: 'Welders', sub: 'Registered', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Due soon', sub: 'Expiry or use', tone: 'accent', loading: allQuery.isLoading, value: countOf((w) => w.qualStatus === 'due_soon') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Not current', sub: 'A qual expired or lapsed', tone: 'accent', loading: allQuery.isLoading, value: countOf((w) => w.qualStatus === 'expired' || w.qualStatus === 'lapsed') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <label className="relative block">
            <span className="sr-only">Search name, stamp or employer</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, stamp or employer"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <NdtPanel
            eyebrow="Welder register"
            title="Welders"
            testId="ndt-welder-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && welders.length > 0
                ? q
                  ? `Showing ${welders.length} of ${countOf(() => true)} welders`
                  : `${welders.length} welders`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={welders}
              columns={columns}
              rail={(w) => welderSkin(w).rail}
              onOpen={(w) => navigate(`/ndt-inspection/welders/${w.id}`)}
              subject="welders"
              testId="ndt-welder"
              minWidth="min-w-[900px]"
              empty={q ? <NdtEmpty>No welder matches “{q}”.</NdtEmpty> : <NdtEmpty>No welders are registered.</NdtEmpty>}
              card={(w) => (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold text-white">{w.name || '—'}</p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">
                      {w.welderStamp || '—'} · {w.employer || '—'} · {welderCodes(w)}
                    </p>
                  </div>
                  <NdtBadge skin={welderSkin(w)} value={w.qualStatus} />
                </div>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canAdd && (
        <WelderDialog open={adding} onClose={() => setAdding(false)} onCreated={(id) => navigate(`/ndt-inspection/welders/${id}`)} />
      )}
    </div>
  );
}
